﻿import React, { useState, useEffect } from 'react';

const TIMER_DURATION = 5;
const TICK_INCREMENT = 0.1;
const TICK_RATE_MS = 100;

const ServiceShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(TIMER_DURATION);

  const services = [
    {
      title: "SuiteCRM Audit & Optimization",
      description: "Get an in-depth review of your existing SuiteCRM setup to maximize performance and ROI.",
      deliverables: [
        "Comprehensive analysis of your current configuration",
        "Identification of inefficiencies and bottlenecks",
        "Actionable recommendations to enhance performance"
      ]
    },
    {
      title: "CRM Strategy Development",
      description: "Craft a robust CRM strategy centered on open-source flexibility and scalability.",
      deliverables: [
        "Process analysis to align SuiteCRM with business goals",
        "Clear roadmap for CRM deployment and integration",
        "Proven best practices for sustained success"
      ]
    },
    {
      title: "Training & Support",
      description: "Empower your team to harness the power of SuiteCRM with confidence and expertise.",
      deliverables: [
        "Tailored hands-on training sessions",
        "Easy-to-follow user documentation",
        "Consistent support to resolve issues and provide guidance"
      ]
    },
    {
      title: "CRM Integration Services",
      description: "Seamlessly integrate SuiteCRM with your existing tools and platforms to create a unified system.",
      deliverables: [
        "Integration with marketing automation platforms",
        "Connection to ERP, e-commerce, and other essential systems",
        "API development for custom integrations"
      ]
    },
    {
      title: "Data Migration Assistance",
      description: "Transition to SuiteCRM without the risk of data loss or downtime effortlessly with dedicated experts.",
      deliverables: [
        "Secure data transfer from legacy systems",
        "Validation and deduplication of data",
        "Ensuring data integrity and compatibility"
      ]
    },
    {
      title: "Workflow Automation",
      description: "Leverage the full potential of SuiteCRM by automating repetitive tasks and processes.",
      deliverables: [
        "Custom workflow design and implementation",
        "Automation of lead tracking, follow-ups, and reporting",
        "Monitoring and optimization of automated workflows"
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= TICK_INCREMENT) {
          return 0;
        }
        const nextValue = Number((prev - TICK_INCREMENT).toFixed(1));
        return nextValue < 0 ? 0 : nextValue;
      });
    }, TICK_RATE_MS);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (timeRemaining !== 0) {
      return;
    }

    const timeout = setTimeout(() => {
      setActiveIndex(current => (current + 1) % services.length);
      setTimeRemaining(TIMER_DURATION);
    }, TICK_RATE_MS);

    return () => clearTimeout(timeout);
  }, [timeRemaining, services.length]);

  const currentService = services[activeIndex];
  const progressPercent = ((TIMER_DURATION - timeRemaining) / TIMER_DURATION) * 100;

  const handleIndicatorClick = (index) => {
    setActiveIndex(index);
    setTimeRemaining(TIMER_DURATION);
  };

  return (
    <div className="crm-services-section">
      <div className="crm-services-layout">
        <div className="crm-services-left">
          <h3 className="crm-services-heading">Our Services</h3>
        </div>
        <div className="crm-services-right">
          <div className="crm-service-showcase">
            <div className="crm-service-content">
              <h4 className="service-title">{currentService.title}</h4>
              <p className="service-description">{currentService.description}</p>
              <div className="service-deliverables">
                <h5>Key Deliverables:</h5>
                <ul>
                  {currentService.deliverables.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="crm-service-navigation">
              <div className="crm-service-indicators">
                {services.map((_, index) => (
                  <div
                    key={index}
                    className={`crm-service-indicator ${index === activeIndex ? 'active' : ''}`}
                    onClick={() => handleIndicatorClick(index)}
                  >
                    <span className="indicator-number">{index + 1}</span>
                    {index === activeIndex && (
                      <svg className="indicator-timer" width="100%" height="100%" viewBox="0 0 50 50">
                        <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2"/>
                        <circle
                          cx="25"
                          cy="25"
                          r="20"
                          fill="none"
                          stroke="#e63946"
                          strokeWidth="2"
                          strokeDasharray="125.6"
                          strokeDashoffset={125.6 - (125.6 * progressPercent / 100)}
                          transform="rotate(-90 25 25)"
                          style={{ transition: `stroke-dashoffset ${TICK_RATE_MS / 1000}s linear` }}
                        />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceShowcase;


